import React, { FC, useMemo } from 'react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';

import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import dataLayer from '@rover/globals/dataLayer';
import { Box, Flex, Image, Link, Text } from '@rover/kibble/core';
import { SpacingSystemType } from '@rover/kibble/core/styledSystemPropTypes';
import useTheme from '@rover/kibble/themes/useTheme';
import { TextSizes } from '@rover/kibble/tokens/build/rover/es6/theme';
import { withOpacity } from '@rover/kibble/utils';
import useI18nContext from '@rover/react-lib/src/context/useI18nContext';
import getBrandSpecificValue from '@rover/shared/js/utils/getBrandSpecificValue';

import TrustPilotLogoDark from './images/trustpilot_logo_dark.svg?url';
import TrustPilotLogoLight from './images/trustpilot_logo_light.svg?url';
import TrustPilotStarsImage from './images/trustpilot_stars.svg?url';
import getTrustpilotUrl from './getTrustPilotUrl';

const STARS_IMAGE_HEIGHTS = ['12px', '17px', '24px'];
const LOGO_HEIGHTS = ['20px', '25px', '32px'];
const LOGO_MARGIN_BOTTOM: SpacingSystemType = ['1x', '2x', '2x'];
const TEXT_SIZES: TextSizes[] = ['50', '100', '300'];

interface TrustpilotBrandContent {
  reviewCount: number;
  ariaLabel: string;
  reviewSlug: string;
}

export enum SizeOverride {
  small,
  medium,
  large,
}

export type TrustPilotProps = React.ComponentProps<typeof Box> & {
  locale?: Locale;
  sizeOverride?: SizeOverride;
  hideOnMobileView?: boolean;
  inverse?: boolean;
};

const trustPilotClickHandler = (): void => {
  dataLayer?.push({
    event: 'trustpilot-widget-click',
  });
};

const Trustpilot: FC<TrustPilotProps> = ({
  locale,
  hideOnMobileView,
  sizeOverride,
  inverse = false, // When true, the text is a light color with a dark opaque background. When false, the text is a dark color with no background
  ...boxProps
}) => {
  const { colors } = useTheme();
  const { locale: i18nLocale } = useI18nContext();

  // Resolved at render time, not module scope: the `t` macro throws on Lingui v5
  // if evaluated before a locale is active (e.g. during Next.js module-eval).
  const { reviewCount, ariaLabel, reviewSlug } = getBrandSpecificValue<TrustpilotBrandContent>({
    rover: {
      reviewCount: 30000,
      ariaLabel: t`External link to Trustpilot page for Rover.com`,
      reviewSlug: 'rover.com',
    },
  });

  const trustpilotUrl = useMemo(() => getTrustpilotUrl(locale, reviewSlug), [locale, reviewSlug]);

  const display = hideOnMobileView ? ['none', 'flex'] : 'flex';
  const formattedTrustpilotReviewCount = reviewCount.toLocaleString(i18nLocale);

  return (
    <Box
      display={display}
      width="auto"
      data-testid="trustpilot-widget-component"
      mt="4x"
      mb="2x"
      px="4x"
      py="2x"
      borderRadius="3px"
      sx={inverse ? { backgroundColor: withOpacity(colors.neutral['900'], 0.5) } : {}}
      {...boxProps}
    >
      <Link
        href={trustpilotUrl.toString()}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={ariaLabel}
        underline={false}
        onPress={trustPilotClickHandler}
        variant={inverse ? 'secondaryContrast' : 'secondary'}
      >
        <Flex justifyContent="center" flexWrap="wrap" alignItems="center" gap="2x">
          {/* A Flex is used here to help center the interior image with the text's line-height */}
          <Flex
            height={
              sizeOverride !== undefined ? STARS_IMAGE_HEIGHTS[sizeOverride] : STARS_IMAGE_HEIGHTS
            }
            sx={{ aspectRatio: '5.3571', position: 'relative' }} // aspect-ratio is used to size the inner image in order to avoid using 'auto' for the image's width property. 'auto' will not be supported in next-js
          >
            <Image
              src={TrustPilotStarsImage}
              description="Trustpilot star ratings"
              webProps={{ eager: true }}
              width="100%"
              height="100%"
            />
          </Flex>

          <Text
            fontWeight="semibold"
            textAlign="center"
            size={sizeOverride !== undefined ? TEXT_SIZES[sizeOverride] : TEXT_SIZES}
            textColor={inverse ? 'secondary_inverse' : 'secondary'}
            sx={{
              whiteSpace: 'nowrap',
              flexBasis: 'max-content',
            }}
          >
            <Trans>{formattedTrustpilotReviewCount}+ reviews on</Trans>
          </Text>
          <Box
            mb={sizeOverride !== undefined ? LOGO_MARGIN_BOTTOM[sizeOverride] : LOGO_MARGIN_BOTTOM}
            height={sizeOverride !== undefined ? LOGO_HEIGHTS[sizeOverride] : LOGO_HEIGHTS}
            sx={{ aspectRatio: '4', position: 'relative' }} // aspect-ratio is used to size the inner image in order to avoid using 'auto' for the image's width property. 'auto' will not be supported in next-js
          >
            <Image
              src={inverse ? TrustPilotLogoLight : TrustPilotLogoDark}
              description="Trustpilot logo"
              webProps={{ eager: true }}
              width="100%"
              height="100%"
            />
          </Box>
        </Flex>
      </Link>
    </Box>
  );
};

export default Trustpilot;
